import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCompanies } from '@/features/companies/hooks/useCompanies'
import { useMe } from '@/features/settings/hooks/useMe'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import type { Company } from '@/types/company.types'
import type { MeRoleId } from '@/types/api.types'

export interface WorkspaceCompanyFilter {
  companies: Company[]
  companiesLoading: boolean
  companiesError: unknown
  selectedCompanyIds: Set<string>
  /** `undefined` when nothing selected, so the API treats it as "all caller's companies". */
  companyIds: string[] | undefined
  companyNameById: Map<string, string>
  toggle: (companyId: string | null) => void
  reset: () => void
}

export interface UseWorkspaceCompanyFilterOptions {
  /** Called after every selection change. Use to reset pagination, etc. */
  onChange?: () => void
}

/**
 * Multi-select company filter for workspace pages.
 *
 * - `companyIds` is shaped to match cross-company query DTOs: `undefined` means "all".
 * - `companyNameById` is the lookup for rendering Company columns.
 */
export function useWorkspaceCompanyFilter(
  opts?: UseWorkspaceCompanyFilterOptions
): WorkspaceCompanyFilter {
  // Scope-all roles see every org company; everyone else gets their member-only list.
  const { data: me } = useMe()
  const scopeAll = useMemo(() => {
    if (!me?.roleId || typeof me.roleId === 'string') return false
    return (me.roleId as MeRoleId).scope === 'all'
  }, [me])
  const source = useCompanies(scopeAll ? undefined : { memberOf: 'me' })
  const companies = (source.data ?? []) as Company[]
  const companiesLoading = source.isLoading
  const companiesError = source.error
  const onlyCompanyId = companies.length === 1 ? companies[0]._id : null
  // Initial selection comes from `?companyId=` (single id or comma-separated). Lets
  // entry points like the company overview pre-filter a workspace page on landing.
  const [selectedCompanyIds, setSelected] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    const v = new URLSearchParams(window.location.search).get('companyId')
    if (!v) return new Set()
    return new Set(v.split(',').map((s) => s.trim()).filter(Boolean))
  })

  const companyIds = useMemo(
    () =>
      selectedCompanyIds.size > 0 ? Array.from(selectedCompanyIds) : undefined,
    [selectedCompanyIds]
  )

  const companyNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of companies) m.set(c._id, c.name)
    return m
  }, [companies])

  const onChange = opts?.onChange

  useEffect(() => {
    if (!onlyCompanyId) return
    setSelected((prev) => {
      if (prev.size === 1 && prev.has(onlyCompanyId)) return prev
      return new Set([onlyCompanyId])
    })
  }, [onlyCompanyId])

  const toggle = useCallback(
    (companyId: string | null) => {
      if (companyId === null) {
        setSelected(onlyCompanyId ? new Set([onlyCompanyId]) : new Set())
      } else {
        setSelected((prev) => {
          const next = new Set(prev)
          if (next.has(companyId)) {
            if (onlyCompanyId === companyId) return next
            next.delete(companyId)
          } else {
            next.add(companyId)
          }
          return next
        })
      }
      onChange?.()
    },
    [onChange, onlyCompanyId]
  )

  const reset = useCallback(() => {
    setSelected(onlyCompanyId ? new Set([onlyCompanyId]) : new Set())
    onChange?.()
  }, [onChange, onlyCompanyId])

  return {
    companies,
    companiesLoading,
    companiesError,
    selectedCompanyIds,
    companyIds,
    companyNameById,
    toggle,
    reset,
  }
}

export interface WorkspaceCompanyChipsProps {
  filter: WorkspaceCompanyFilter
}

/** Standard chip strip for workspace pages. Renders unconditionally. */
export function WorkspaceCompanyChips({
  filter,
}: WorkspaceCompanyChipsProps): React.ReactElement | null {
  if (filter.companiesLoading || filter.companies.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <CompanyChips
        companies={filter.companies.map((c) => ({
          _id: c._id,
          name: c.name,
          color: (c as unknown as { color?: string }).color,
        }))}
        selectedCompanyIds={filter.selectedCompanyIds}
        onToggle={filter.toggle}
      />
    </div>
  )
}

WorkspaceCompanyChips.displayName = 'WorkspaceCompanyChips'
